/**
 * What a dashboard may claim about its own governance.
 *
 * "Governed" means the figures ran through the semantic layer, not that the
 * model is certified. A dashboard is only as endorsed as its least endorsed
 * panel, and an unknown endorsement is never read as certified. The API applies
 * the same rule to exports, so the screen and the file say the same thing.
 */
import type { PanelResult } from "../types";

const LABELS: Record<string, string> = {
  certified: "Certified",
  approved_with_exceptions: "Approved with exceptions, not certified",
  promoted: "Promoted, not certified",
};
const STANDING: Record<string, number> = {
  certified: 3,
  approved_with_exceptions: 2,
  promoted: 1,
};
export const NOT_RECORDED = "Endorsement not recorded; treat as not certified";
export const MAX_SHOWN_PERIOD_NOTES = 4;

interface Governance {
  endorsement?: string | null;
  periodNotes?: unknown;
}

/**
 * Where each result states its governance: a saved result on its provenance, a
 * streamed preview on the result itself. A result carrying neither has not
 * executed and says nothing either way.
 */
function provenances(results: Iterable<PanelResult | undefined>): Governance[] {
  return [...results].flatMap((item): Governance[] => {
    if (item?.provenance) return [item.provenance];
    if (item?.status === "ok" && "endorsement" in item) return [item];
    return [];
  });
}

/** The weakest endorsement, or null while no panel has executed yet. */
export function dashboardEndorsementLabel(
  results: Iterable<PanelResult | undefined>,
): string | null {
  const executed = provenances(results);
  if (executed.length === 0) return null;
  const endorsements = executed.map((item) => item.endorsement ?? "");
  if (endorsements.some((value) => !(value in STANDING))) return NOT_RECORDED;
  const weakest = endorsements.reduce((low, value) =>
    (STANDING[value] ?? 0) < (STANDING[low] ?? 0) ? value : low,
  );
  return LABELS[weakest] ?? NOT_RECORDED;
}

/** Distinct unfinished-period notes across panels, in first-seen order. */
export function dashboardPeriodNotes(
  results: Iterable<PanelResult | undefined>,
): string[] {
  const notes = provenances(results).flatMap((item) =>
    Array.isArray(item.periodNotes)
      ? item.periodNotes.filter((note): note is string => typeof note === "string" && note.trim() !== "")
      : [],
  );
  return [...new Set(notes)].slice(0, MAX_SHOWN_PERIOD_NOTES);
}
